/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FC, useMemo, useState, useEffect, useCallback, memo } from 'react'

import {
  CryptographyWrapper,
  useCryptographyStore,
} from '@builddocs/cryptography-js'
import {
  UUID,
  ApprovalState,
  SignatureState,
  useCurrentUser,
  assigneesStateFamily,
  useCoordinator,
  useVersionOf,
  useGetMRFList,
  getPoaDataToSign,
  useCheckOperation,
} from '@builddocs/data-management'
import { InvitationExternalSigning } from '@builddocs/feature-external-sign'
import { AOSRType, SecondaryActType } from '@builddocs/filestore-api'
import { DeviationDialog } from '@builddocs/mui'
import {
  statusColor,
  statusIcon,
  theStateWithDescription,
  UserInfoPopoverWithStatus,
} from '@builddocs/mui'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import format from 'date-fns/format'
import { toast } from 'react-toastify'
import { useRecoilValueLoadable } from 'recoil'

import { getAssignees } from '../Utils/InspectAndSigningAct'
import { prepareDataForSigningNew } from '../Utils/prepare-data-for-signing-new'
import { sendSignedData } from '../Utils/send-signed-data'

const PoaSignedIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_20167_107334)">
      <circle cx="8" cy="8" fill="#43A047" r="8" />
      <path d="M8 8H16V12.5C16 14.433 14.433 16 12.5 16H8V8Z" fill="#43A047" />
      <path d="M2.5 8L6 11.5L12.5 5" stroke="white" strokeWidth="1.5" />
      <path
        d="M10 12.5C10 12.2239 10.2239 12 10.5 12H14.5C14.7761 12 15 12.2239 15 12.5V12.75C15 12.8881 14.8881 13 14.75 13H10.25C10.1119 13 10 12.8881 10 12.75V12.5Z"
        fill="white"
      />
      <path
        d="M11 9.75C11 9.33579 11.3358 9 11.75 9H13.25C13.6642 9 14 9.33579 14 9.75V12C14 12.5523 13.5523 13 13 13H12C11.4477 13 11 12.5523 11 12V9.75Z"
        fill="white"
      />
      <path
        d="M12 10.25C12 10.1119 12.1119 10 12.25 10H12.75C12.8881 10 13 10.1119 13 10.25V12H12V10.25Z"
        fill="#43A047"
      />
      <path
        d="M10 12.75L11 13V13.85C11 13.9328 10.9328 14 10.85 14H10.15C10.0672 14 10 13.9328 10 13.85V12.75Z"
        fill="white"
      />
      <path
        d="M15 12.75L14 13V13.9302C14 13.9688 14.0312 14 14.0698 14C14.5835 14 15 13.5835 15 13.0698V12.75Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_20167_107334">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
)

interface IChooseSignActionProps {
  act: AOSRType | SecondaryActType
  handleToSigning: (isEmbedded?: boolean) => void
  handleDeviate: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
  ) => void
  userEmploymentId: UUID
  canSign: boolean
  canSendToSigning: boolean
  isDisabled: boolean
  isReturnedFromGIS?: boolean
  toReopenInspection: boolean
  canShowSigningActions: boolean
  initActSigning: () => void
  refreshAfterSign: () => void
  forceContractId?: UUID
}

export const ChooseSignAction: FC<IChooseSignActionProps> = memo(
  ({
    act,
    handleToSigning,
    handleDeviate,
    userEmploymentId,
    canSendToSigning,
    isDisabled,
    initActSigning,
    isReturnedFromGIS,
    toReopenInspection,
    refreshAfterSign,
    canShowSigningActions,
    forceContractId,
  }: IChooseSignActionProps) => {
    const { cryptography_version, poaIsEnabled } = useVersionOf()
    const { user } = useCurrentUser()
    const [employmentId, setEmploymentId] = useState<string | null>(null)
    const [currentProxyId, setCurrentProxyId] = useState<string | null>(null)

    const { contractId, projectId } = useCoordinator() as {
      contractId: string
      projectId: string
    }
    const assigneesTemp = useRecoilValueLoadable(
      assigneesStateFamily(contractId),
    )

    const { checkOperation } = useCheckOperation()

    const signingState = useMemo(() => {
      const states = act.assignees?.map(user => user.signatureState)

      if (act.approvalStatus !== ApprovalState.SIGNING) {
        if (states.includes(SignatureState.REJECTED)) {
          return 'Отклонён'
        } else if (
          !states.includes(SignatureState.PENDING) ||
          !states.includes(SignatureState.UNDEFINED)
        ) {
          return 'Подписан'
        } else {
          return 'На подписании'
        }
      } else {
        return 'На подписании'
      }
    }, [act])

    const { selectedCertificate } = useCryptographyStore()
    const { data: mrfList } = useGetMRFList({
      userId: user.userId,
      enabled: poaIsEnabled,
    })

    const [isGeneratingSignContent, setIsGeneratingSignContent] =
      useState(false)
    const [isFaultGeneration, setIsFaultGeneration] = useState(false)
    const [signContent, setSignContent] = useState<{
      hash: string | null
      actRegistryDigest: string | null
    }>({ hash: null, actRegistryDigest: null })

    const onGenerateStringForSigning = useCallback(async () => {
      if (!selectedCertificate) return

      const poa = currentProxyId
        ? mrfList?.poas?.find(poa => poa?.poaIdInternal === currentProxyId)
        : null
      const poaData = getPoaDataToSign({
        proxyId: currentProxyId,
        poa,
        poaIsEnabled,
      })

      return prepareDataForSigningNew({
        act,
        user,
        certificateDateStart: format(
          selectedCertificate.ValidFromDate,
          'yyyy-MM-dd',
        ),
        certificateDateEnd: format(
          selectedCertificate.ValidToDate,
          'yyyy-MM-dd',
        ),
        imprint: selectedCertificate.Thumbprint,
        keyIssuer: selectedCertificate.Issuer.CN,
        facePhoto: '',
        keySerialNumber: selectedCertificate?.SerialNumber,
        signerName: selectedCertificate.Name,
        isEmbedded: true,
        projectId: projectId,
        contractId: contractId,
        selectedSigners: getAssignees(assigneesTemp, act.workType).signers,
        ...poaData,
      })
    }, [
      currentProxyId,
      selectedCertificate,
      act,
      user,
      projectId,
      contractId,
      assigneesTemp,
      mrfList,
      poaIsEnabled,
    ])

    const generateSignContent = useCallback(async () => {
      if (!selectedCertificate) return []
      setIsGeneratingSignContent(true)
      try {
        const result = await onGenerateStringForSigning()

        if (result?.hash) {
          setIsFaultGeneration(false)
          setSignContent({
            hash: result.hash,
            actRegistryDigest: result.actRegistryDigest || null,
          })
          return [result.hash, result.actRegistryDigest].filter(
            (item): item is string => item !== null,
          )
        }
        return []
      } catch (error) {
        setIsFaultGeneration(true)
        toast.error('Ошибка при генерации данных для подписания')
        return []
      } finally {
        setIsGeneratingSignContent(false)
      }
    }, [selectedCertificate, onGenerateStringForSigning])

    useEffect(() => {
      if (selectedCertificate && employmentId === userEmploymentId) {
        generateSignContent()
      }
    }, [
      selectedCertificate,
      employmentId,
      userEmploymentId,
      generateSignContent,
    ])

    const onSuccessSign = useCallback(
      async (hash: string[], proxyId: string | null) => {
        if (!selectedCertificate) return
        const poa = mrfList?.poas?.find(poa => poa?.poaIdInternal === proxyId)
        const poaData = getPoaDataToSign({ proxyId, poa, poaIsEnabled })

        await sendSignedData({
          act,
          user,
          signedHash: signContent.hash ? hash[0] : '',
          actRegistryBase64signature: signContent.actRegistryDigest
            ? hash[1]
            : '',
          imprint: selectedCertificate.Thumbprint,
          keyIssuer: selectedCertificate.Issuer.CN,
          facePhoto: '',
          keySerialNumber: selectedCertificate?.SerialNumber,
          signerName: selectedCertificate.Name,
          refreshAct: refreshAfterSign,
          ...poaData,
        })
        return true
      },
      [selectedCertificate, act, user, refreshAfterSign, signContent],
    )

    const canInitialSigning = act.externalId ? toReopenInspection : true

    if (!act.closingId) {
      return null
    }

    // логику этого метода можно вынести в отдельную функцию
    if (
      canSendToSigning &&
      act.approvalStatus === ApprovalState.APPROVED &&
      (canInitialSigning || isReturnedFromGIS)
    ) {
      return (
        <Stack alignItems="flex-start" spacing={1}>
          <Typography gutterBottom sx={{ color: '0,0,0,0.6', fontSize: 12 }}>
            На подписание:
          </Typography>
          <Button
            key={1}
            disabled={isDisabled || !canShowSigningActions}
            size="small"
            onClick={() => initActSigning()}
          >
            Встроенной подписью
          </Button>
        </Stack>
      )
    }

    const handleToSigningClick = () => {
      checkOperation({
        operationId: 'SIGN_ACT',
        operationCallback: () => handleToSigning(),
      })
    }

    if (act.assignees.length > 0) {
      return (
        <Stack>
          <Typography gutterBottom sx={{ color: '0,0,0,0.6', fontSize: 12 }}>
            {signingState}
          </Typography>
          {act.assignees?.map(assignee => {
            const { employmentId, signatureState, signingDeadlineDate } =
              assignee
            const withPoa = (assignee as any)?.withPoa

            if (
              signatureState === SignatureState.PENDING &&
              employmentId === userEmploymentId
            ) {
              return (
                <Paper
                  key={employmentId}
                  elevation={3}
                  sx={{ p: 0.5, my: 0.5, mx: -0.5 }}
                >
                  <UserInfoPopoverWithStatus
                    key={act.id}
                    color="#333333"
                    deadline={signingDeadlineDate}
                    icon={statusIcon(signatureState)}
                    status={theStateWithDescription(signatureState)}
                    statusColor={statusColor(signatureState)}
                    userEmploymentId={employmentId}
                  />
                  <Stack
                    key={2}
                    direction="row"
                    justifyContent="center"
                    spacing={2}
                  >
                    {cryptography_version === 'v1' && (
                      <Button
                        key={2}
                        color="accepted"
                        disabled={isDisabled}
                        size="small"
                        onClick={handleToSigningClick}
                      >
                        Подписать
                      </Button>
                    )}

                    {cryptography_version === 'v2' && (
                      <CryptographyWrapper
                        currentUser={{
                          firstName: user?.firstName || '',
                          lastName: user?.lastName || '',
                          surname: '',
                          middleName: user?.middleName || '',
                          userId: user?.userId || '',
                        }}
                        forceDisableSignButton={
                          isGeneratingSignContent || isFaultGeneration
                        }
                        proxy={mrfList?.poas}
                        proxyIsEnabled={poaIsEnabled}
                        signContent={
                          generateSignContent as () => Promise<string[]>
                        }
                        signMode="multi"
                        signType="string"
                        onSuccess={(hash, proxyId) => {
                          setCurrentProxyId(proxyId)
                          return onSuccessSign(hash, proxyId)
                        }}
                      >
                        {({ onOpen }) => (
                          <Button
                            key={2}
                            color="accepted"
                            disabled={isGeneratingSignContent}
                            size="small"
                            onClick={() => {
                              onOpen()
                              setEmploymentId(employmentId)
                            }}
                          >
                            Подписать
                          </Button>
                        )}
                      </CryptographyWrapper>
                    )}

                    <DeviationDialog
                      additionalInfo={{
                        id: act.id,
                        entityType: 'act',
                      }}
                      approvalStatus={act.approvalStatus}
                      complect="sign"
                      documentType={act.actType}
                      initialInformation={act.name}
                      isDisabled={isDisabled}
                      subtitle={`Для документа ${act.name}`}
                      title="Записать отклонение"
                      onSubmit={handleDeviate}
                    />
                  </Stack>
                </Paper>
              )
            }

            return (
              <Stack key={employmentId}>
                <UserInfoPopoverWithStatus
                  key={act.id}
                  color="#333333"
                  deadline={
                    signatureState === SignatureState.PENDING
                      ? signingDeadlineDate
                      : undefined
                  }
                  icon={
                    signatureState === SignatureState.SIGNED && withPoa ? (
                      <PoaSignedIcon />
                    ) : (
                      statusIcon(signatureState)
                    )
                  }
                  status={theStateWithDescription(signatureState)}
                  statusColor={statusColor(signatureState)}
                  userEmploymentId={employmentId}
                />
              </Stack>
            )
          })}

          {canInitialSigning && (
            <Box>
              {/* @ts-expect-error затычка */}
              <InvitationExternalSigning
                actId={act.id}
                actType={act.actType}
                assignees={act.assignees}
                forceContractId={forceContractId}
                iconButton={false}
                signType="ACT"
              />
            </Box>
          )}
        </Stack>
      )
    } else {
      return null
    }
  },
)

ChooseSignAction.displayName = 'ChooseSignAction'

export default ChooseSignAction
